// src/components/reader/lessonMeta.js
//
// Pure helpers behind the lesson layout (lesson-3a-layout). No DOM, no React, no
// data access -- so every decision the layout makes is unit-testable and identical
// on the server-rendered preview pages and in the reader.

// Keys whose string values are NOT prose: ids, nav metadata, URLs, alt text. Counting
// them would inflate the read estimate (a YouTube URL is 1 "word" that takes no time)
// or, worse, make a media-only section claim a reading time it does not have.
// Note `image` is deliberately ABSENT: the container recurses so its caption still
// counts as prose -- only the URL and alt text inside it are skipped.
const NON_PROSE_KEYS = new Set([
    'id', 'unit', 'video', 'introVideo', 'imageUrl', 'alt', 'src',
    'audioUrl', 'relatedTextId', 'kind', 'sectionKind',
]);

// Recursively count words in a content value. Strings contribute their whitespace-
// separated tokens; arrays and plain objects recurse; everything else contributes 0.
// A denylist (above) rather than an allowlist of block names on purpose: a new block
// kind should be counted by default, not silently ignored until someone remembers to
// register it.
export function countWords(value, key) {
    if (typeof key === 'string' && NON_PROSE_KEYS.has(key)) return 0;
    if (typeof value === 'string') {
        const t = value.trim();
        return t ? t.split(/\s+/).length : 0;
    }
    if (Array.isArray(value)) return value.reduce((n, v) => n + countWords(v), 0);
    if (value && typeof value === 'object') {
        return Object.keys(value).reduce((n, k) => n + countWords(value[k], k), 0);
    }
    return 0;
}

// Average adult reading speed for study prose. 200 wpm is the conservative end of the
// usual 200-250 range -- a lesson that takes LESS time than promised is the kind of
// wrong that costs nothing.
export const WORDS_PER_MINUTE = 200;

// "~N min read" for a section's blocks. Always at least 1: a lesson that exists takes
// some time to read, and "~0 min" reads like an error.
export function readingMinutes(blocks) {
    const words = countWords(blocks);
    return Math.max(1, Math.round(words / WORDS_PER_MINUTE));
}

// Should this body be collapsed behind "Read more"?
//
// A character estimate, not a DOM measure: the decision has to be stable across the
// three reading scales and both breakpoints (a body that collapses on one and not the
// other looks like a bug), and a pure function is testable. ~6 lines at the reader's
// measure (max-w-2xl, ~70ch) is about 420 characters. An explicit newline counts as
// filling the rest of its line, so a short poem-shaped body collapses like the prose
// of the same visual height.
export const COLLAPSE_CHARS = 420;
export const COLLAPSE_LINE_CHARS = 70;

export function shouldCollapseBody(text, limit = COLLAPSE_CHARS) {
    if (typeof text !== 'string') return false;
    const t = text.trim();
    if (!t) return false;
    const cost = t.split('\n').reduce((n, line) => n + Math.max(line.length, COLLAPSE_LINE_CHARS), 0);
    return cost > limit;
}

// Flatten the reader's nav into ONE ordered list, so Prev/Next walk the lesson the way
// the sidebar reads: every item of every group in order, then the practice entries.
// `navGroups` is the readers' existing shape ([{ key, label, items:[{id, content}] }]);
// `practiceNav` is [{ id, kind, label }]. Returns entries the readers can act on
// directly -- an item entry carries the group key its selection needs.
export function flattenLessonOrder(navGroups, practiceNav) {
    const out = [];
    for (const g of (Array.isArray(navGroups) ? navGroups : [])) {
        if (!g || !Array.isArray(g.items)) continue;
        for (const it of g.items) {
            if (!it || !it.id) continue;
            out.push({
                kind: 'item',
                id: it.id,
                groupKey: g.key,
                title: (it.content && it.content.title) || g.label || '',
            });
        }
    }
    for (const p of (Array.isArray(practiceNav) ? practiceNav : [])) {
        if (!p || !p.id) continue;
        out.push({ kind: 'practice', id: p.id, groupKey: null, title: p.label || '' });
    }
    return out;
}

// The entries either side of the current one. Either may be null (the ends of the
// lesson), and an unknown id yields both null rather than guessing a position --
// a wrong Prev/Next is worse than none.
export function prevNextFor(order, currentId) {
    const list = Array.isArray(order) ? order : [];
    const i = list.findIndex((e) => e && e.id === currentId);
    if (i < 0) return { prev: null, next: null, index: -1, total: list.length };
    return {
        prev: i > 0 ? list[i - 1] : null,
        next: i < list.length - 1 ? list[i + 1] : null,
        index: i,
        total: list.length,
    };
}
